import { AuthTokens, fetchAuthSession } from 'aws-amplify/auth';
import { getApolloClient } from '@/apollo/apollo-client-instance';
import { amplifyConfig } from '../../amplify-config';
import { CreateOAuthCodeMutation } from '../routes/_auth.auth.login/create-oauth-code-mutation';

function extractUsernameFromIdToken(idToken: string): string | null {
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return (
      payload['cognito:username'] ||
      payload.preferred_username ||
      payload.sub ||
      null
    );
  } catch {
    return null;
  }
}

function getCookieValue(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }
  return null;
}

function getRefreshTokenFromStorage(idToken: string): string | null {
  try {
    const username = extractUsernameFromIdToken(idToken);

    if (!username) {
      return null;
    }

    const keyPrefix = `CognitoIdentityServiceProvider.${amplifyConfig.Auth.Cognito.userPoolClientId}`;
    const refreshTokenKey = `${keyPrefix}.${username}.refreshToken`;

    // With SSR, tokens are stored in cookies
    let refreshToken = getCookieValue(refreshTokenKey);

    // Also try URL-encoded version of the key
    if (!refreshToken) {
      refreshToken = getCookieValue(encodeURIComponent(refreshTokenKey));
    }

    return refreshToken;
  } catch (error) {
    console.error('Failed to get refresh token from storage:', error);
    return null;
  }
}

export async function createAuthCodeFromSession(
  authTokens?: AuthTokens | undefined,
): Promise<string> {
  const session = await fetchAuthSession();

  const tokens = authTokens ?? session?.tokens;

  if (!tokens?.idToken || !tokens?.accessToken) {
    throw new Error('No tokens available in session');
  }

  const idTokenString = tokens.idToken.toString();
  const accessTokenString = tokens.accessToken.toString();

  // Get refresh token from cookies (SSR uses cookie storage)
  const refreshToken = getRefreshTokenFromStorage(idTokenString);

  const apolloClient = getApolloClient();

  const { data } = await apolloClient.mutate({
    mutation: CreateOAuthCodeMutation,
    variables: {
      input: {
        idToken: idTokenString,
        accessToken: accessTokenString,
        refreshToken: refreshToken,
      },
    },
  });

  if (!data?.createOAuthCode?.code) {
    throw new Error('Failed to create auth code');
  }

  return data.createOAuthCode.code;
}
